/* eslint-disable no-unused-vars */
import { createSlice } from '@reduxjs/toolkit';
import { format, parseISO } from 'date-fns';
const dateFormat = 'yyyy/MM/dd';
// TO-DO correct the key to match old platform?
const STORAGE_KEY = '___locache___/reports';

const getLocalStorageItem = ({ key, field, initialValue }) => {
  try {
    const item = window.localStorage.getItem(key);
    if (JSON.parse(item)?.[field]) {
      return JSON.parse(item)?.[field];
    }
    if (initialValue) {
      saveLocalStorageItem({ key, field, value: initialValue });
      return getLocalStorageItem({ key, field });
    }
  } catch (error) {
    console.error(error);
    return '';
  }
};

const getLocalStorageObj = ({ key }) => {
  try {
    const item = window.localStorage.getItem(key);
    return item ? JSON.parse(item) : {};
  } catch (error) {
    console.error(error);
    return {};
  }
};

function getLastMonthStart() {
  const date = new Date();
  date.setMonth(date.getMonth() - 1);
  date.setDate(1);
  date.setHours(0, 0, 0, 0);
  return date;
}

function getLastMonthEnd() {
  const date = new Date();
  date.setDate(1);
  // date.setHours(23, 59, 59, 999);
  date.setDate(0);
  return date;
}

const saveLocalStorageItem = ({ key, field, value }) => {
  const objFromLS = getLocalStorageObj({ key });
  try {
    const updatedValue = { ...objFromLS, [field]: value };
    window.localStorage.setItem(key, JSON.stringify(updatedValue));
  } catch (error) {
    console.error(error);
  }
};

const formatDate = (date) => {
  return new Date(format(date, dateFormat)).toISOString();
};

function getDaysFromRange(range) {
  if (range === 'Last 7 Days' || range === 'Last 14 Days' || range === 'Last 30 Days') {
    const match = range.match(/Last (\d+) Days/);
    if (match) {
      return parseInt(match[1], 10);
    }
  }
  return false;
}

const createDefaultSelectedDateRange3 = () => {
  let periodName = getLocalStorageItem({ key: STORAGE_KEY, field: 'dateRangeNameReports' });

  if (!periodName) {
    periodName = 'This Month';
  }
  let startDate;
  let endDate;
  if (periodName === 'Custom') {
    const dateFrom = getLocalStorageItem({ key: STORAGE_KEY, field: 'dateFrom' });
    const dateTo = getLocalStorageItem({ key: STORAGE_KEY, field: 'dateTo' });
    startDate = formatDate(dateFrom);
    endDate = formatDate(dateTo);
  }
  if (periodName === 'Today') {
    startDate = formatDate(new Date());
    endDate = formatDate(new Date());
  }
  if (periodName === 'Yesterday') {
    const currentDate = new Date();
    const currentDay = currentDate.getDate();
    currentDate.setDate(currentDay - 1);
    startDate = formatDate(currentDate);
    endDate = formatDate(currentDate);
  }
  if (periodName === 'This Week') {
    const startOfWeek = new Date();
    const dayOfWeek = startOfWeek.getDay();
    const diff = startOfWeek.getDate() - dayOfWeek + (dayOfWeek === 0 ? -6 : 1);
    startOfWeek.setDate(diff);
    startDate = formatDate(startOfWeek);
    endDate = formatDate(new Date());
  }
  if (periodName === 'This Month') {
    startDate = formatDate(new Date().setDate(1));
    endDate = formatDate(new Date());
  }
  if (periodName === 'Last Month') {
    const daysCount = getDaysFromRange('Last 30 Days');
    const currentDate = new Date();
    currentDate.setDate(currentDate.getDate() - daysCount);
    startDate = formatDate(currentDate);
    endDate = formatDate(new Date());
  }
  if (periodName === 'Last Full Month') {
    startDate = formatDate(getLastMonthStart());
    endDate = formatDate(getLastMonthEnd());
  }
  if (getDaysFromRange(periodName)) {
    const daysCount = getDaysFromRange(periodName);
    const currentDate = new Date();
    currentDate.setDate(currentDate.getDate() - daysCount);
    startDate = formatDate(currentDate);
    endDate = formatDate(new Date());
  }
  saveLocalStorageItem({
    key: STORAGE_KEY,
    field: 'dateFrom',
    value: startDate,
  });
  saveLocalStorageItem({
    key: STORAGE_KEY,
    field: 'dateTo',
    value: endDate,
  });
  return {
    from: format(parseISO(startDate), dateFormat),
    to: format(parseISO(endDate), dateFormat),
  };
};

const createDefaultDateRangeNameReports = () => {
  const dateRangeNameReports = getLocalStorageItem({
    key: STORAGE_KEY,
    field: 'dateRangeNameReports',
    initialValue: 'This Month',
  });
  return dateRangeNameReports;
};

const createDefaultSelectedMetrics = () => {
  const selectedMetrics = getLocalStorageItem({
    key: STORAGE_KEY,
    field: 'selectedMetrics',
    initialValue: {
      installs: true,
      impressions: true,
      clicks: true,
      ctr: true,
      earning: true,
      eCPM: true,
    },
  });
  return selectedMetrics;
};

const getDefaultState = () => {
  return {
    selectedDateRangeReports: createDefaultSelectedDateRange3(),
    dateRangeNameReports: createDefaultDateRangeNameReports(),
    isChartHiddenReports: false,
    isChartDisabledReports: false,
    selectedMetricsReports: createDefaultSelectedMetrics(),
  };
};

const reportsSlice = createSlice({
  name: 'reports',
  initialState: getDefaultState(),
  reducers: {
    updateProperty: (state, action) => {
      state[action.payload.propField] = action.payload.newValue;
    },
    updateDateRangeName: (state, action) => {
      saveLocalStorageItem({
        key: STORAGE_KEY,
        field: 'dateRangeNameReports',
        value: action.payload,
      });
      state.dateRangeNameReports = action.payload;
    },
    updateTimeRange: (state, action) => {
      saveLocalStorageItem({
        key: STORAGE_KEY,
        field: 'dateFrom',
        value: formatDate(action.payload.from),
      });
      saveLocalStorageItem({
        key: STORAGE_KEY,
        field: 'dateTo',
        value: formatDate(action.payload.to),
      });
      state.selectedDateRangeReports = action.payload;
    },
    setRowData: (state, action) => {
      state.gridRowData = action.payload;
    },
    saveSelectedAppsToLocalStorage: (state, action) => {
      saveLocalStorageItem({
        key: STORAGE_KEY,
        field: 'selectedApps',
        value: action.payload,
      });
    },
    saveSelectedMetricsToLocalStorage: (state, action) => {
      saveLocalStorageItem({
        key: STORAGE_KEY,
        field: 'selectedMetrics',
        value: action.payload,
      });
    },
  },
});

export const reportsActions = reportsSlice.actions;
reportsActions.REPORTS_STORAGE_KEY = STORAGE_KEY;
export default reportsSlice.reducer;
